/**
 * OKS-855 — Single source of truth for the brand-cover aspect ratio.
 *
 * Until this ticket the brand cover was framed as a shallow 3:1 strip
 * (`aspect-[3/1]`, hardcoded three times inside `BrandCoverBand`), which
 * read as "too small" on the creator home (OKS-799 tester report / CTPO
 * directive). OKS-855 retargets it to 3:2 — the SAME ratio mission covers
 * already use (`lib/mission-cover.ts`) — and crops-on-upload to that ratio
 * so the framed area is exactly what every surface renders
 * (client-crop-authoritative, mirroring the shipped OKS-697 mission /
 * OKS-768 masterclass pattern). The ratio lives HERE, once, so a future
 * ratio change is a single edit and no surface can silently drift back to
 * 3:1 — `BrandCoverBand` imports the symbol rather than re-typing the class.
 *
 * Two shapes are exported because two consumers need different forms:
 *
 *   - `BRAND_COVER_ASPECT_CLASS` — the Tailwind utility class string
 *     consumed by the display container (`cn(BRAND_COVER_ASPECT_CLASS, …)`
 *     in `BrandCoverBand`'s `default` variant). It MUST remain a literal
 *     `"aspect-[3/2]"` string in THIS file: Tailwind v4's JIT scans source
 *     for arbitrary-value utilities, so the literal has to exist somewhere
 *     it can see it. Keeping it here (and only here) is what makes the "one
 *     edit point" guarantee real — the band is the single display choke
 *     point (every default-variant consumer routes through it), so this is
 *     the ONLY place the ratio is applied. LQE's Vitest parity test scans
 *     the band for this exact symbol + zero `aspect-[3/1]` literals.
 *
 *   - `BRAND_COVER_ASPECT_RATIO` — the numeric ratio (width / height) fed to
 *     `react-easy-crop`'s `aspect` prop and the crop-area math via the shared
 *     `CoverCropper`. Kept in lockstep with the class (3 / 2 = 1.5).
 *
 * NOTE — the `identity` band variant (invite-landing premium hero, OKS-383)
 * is intentionally NOT aspect-locked (bespoke tall `min-h` hero) and does
 * NOT consume this token; it renders the same cover via `object-cover`.
 */

/**
 * Tailwind aspect-ratio utility for the brand-cover `default` band.
 * Literal string (see module note) — do not build it dynamically.
 */
export const BRAND_COVER_ASPECT_CLASS = "aspect-[3/2]"

/**
 * Numeric brand-cover ratio (width / height = 3 / 2 = 1.5). Feeds the shared
 * `CoverCropper` `react-easy-crop` `aspect` and the canvas crop-area math.
 */
export const BRAND_COVER_ASPECT_RATIO = 3 / 2
